import React, { useCallback, useState } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import moment from 'moment'
import { flatten, isNil, noop } from 'lodash-es'
import {
  Currency,
  DealActions,
  DealDetail,
  DealServicingStartTerminateBulkArgs,
  DealStatus,
  NegotiationFieldMeta,
  UserPreference,
  ValidationIssue
} from 'api'
import { ModalFooter, ModalWindow, Table } from 'view/components/panels'
import { DialogField, DialogRow } from 'view/components/inputs'
import { useOnMount } from 'view/hooks'
import { TradingDatePickerField } from '../../entity'
import { ValidationIssuesContainer } from 'view/shared/error'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { canServicingAccept, canServicingCancel, canServicingSend } from 'view/routes/deals/components/utils'
import { DateLike, notNilFilter, toLocalDateString } from 'utils'
import { createInvalidValueValidationIssue, getDateEditorFormat, Severity } from 'model'
import { createFilteredDealDetailsSelector } from 'selectors/dealSelectors'
import { platformTradeDateSelector } from 'selectors/platformSelectors'
import { createUserPreferenceSelector } from 'selectors/userPreferenceSelectors'
import { Locale, Translation } from 'locale'
import * as constants from 'view/styles/constants'
import { Content, FieldsContainer, TableCell, TableCellInput } from '../servicingDialogStyles'
import { ModalConsumerProps, NotificationType, withModal } from '../../modal'
import { useDealDetailsEffectiveDateInfo } from '../useGetEffectiveDateInfo'
import { options, useTerminationColumns } from './ServicingTerminationGridModel'

export type ServicingTerminationDialogContainerProps = OwnProps & StateProps & DispatchProps

interface OwnProps {
  dealIds: string[]
  readOnly?: { [P in NegotiationFieldMeta]?: boolean }
  onClose?: () => unknown
}

interface StateProps {
  dealDetails: DealDetail[]
  platformTradeDate: DateLike | null
  userPreference: UserPreference
}

interface DispatchProps {
  onServicingStartTerminateBulk: (args: DealServicingStartTerminateBulkArgs) => unknown
  onUpdateField: typeof DealActions.servicingUpdateAttributes
  onRevert: typeof DealActions.servicingRevertAttributes
  onSend: (dealId: string) => void
  onCancel: typeof DealActions.servicingCancel
}

export interface ServicingTerminationDialogComponentProps extends Omit<ServicingTerminationDialogContainerProps, 'onSend'> {
  title: string
  confirmBtnText: string
  canSend: (d: DealDetail) => boolean
  onSend: (dealIds: string[]) => Promise<boolean>
}

export const ServicingTerminationDialogComponent: React.FC<ServicingTerminationDialogComponentProps> = props => {
  const {
    title,
    confirmBtnText,
    dealDetails,
    platformTradeDate,
    canSend,
    onServicingStartTerminateBulk,
    onClose = noop,
    userPreference,
    onUpdateField,
    onCancel,
    onSend
  } = props

  const firstDealDetail = dealDetails[0] ? dealDetails[0] : null

  if (dealDetails.some(dealDetail => dealDetail.deal.currency !== firstDealDetail?.deal.currency)) {
    throw new Error('Bulk rerate only applies to deals with the same currency')
  }

  const {
    minEffectiveDate: globalMinEffectiveDate,
    maxEffectiveDate: globalMaxEffectiveDate,
    isBeforeCutOffTime
  } = useDealDetailsEffectiveDateInfo(dealDetails, true)

  const [effectiveDate, setEffectiveDate] = useState<string | null>(toLocalDateString(globalMinEffectiveDate))

  const validationIssues: ValidationIssue[] = [
    platformTradeDate && effectiveDate && moment(effectiveDate).isSame(platformTradeDate, 'day') && !isBeforeCutOffTime
      ? createInvalidValueValidationIssue(
          'effectiveDate',
          Locale.translate(Translation.SC_EFFECTIVE_DATE_TODAY_WARNING_MESSAGE),
          Severity.WARN
        )
      : null
  ]
    .concat(flatten(dealDetails.map(dealDetail => dealDetail.servicingSideDetails?.validationIssues ?? [])))
    .filter(notNilFilter)

  const hasValidationErrors = validationIssues.filter(validationIssue => validationIssue.severity === Severity.ERROR).length > 0

  const handleUpdateField = useCallback(
    ({ dealId, value, field, rawValue }: { dealId: string; value: any; field: NegotiationFieldMeta; rawValue?: any }) => {
      onUpdateField({
        dealId,
        field,
        updatedValue: value,
        updatedRawValue: rawValue,
        decisionMakerOrientedByNegotiationSide: true,
        meta: { haircutSign: userPreference.haircutSign },
        revertFirst: false
      })
    },
    [onUpdateField, userPreference.haircutSign]
  )

  const columns = useTerminationColumns(handleUpdateField)

  const handleSetAllEffectiveDates = () => {
    dealDetails.forEach(dealDetail => {
      handleUpdateField({ dealId: dealDetail.deal.dealId, value: effectiveDate, field: NegotiationFieldMeta.END_DATE })
    })
  }

  const handleSend = async () => {
    if (await onSend(dealDetails.map(({ deal }) => deal.dealId))) {
      onClose()
    }
  }

  const handleCancel = async () => {
    dealDetails.forEach(dealDetail => {
      if (canServicingCancel(dealDetail)) {
        onCancel(dealDetail.deal.dealId)
      }
    })
    onClose()
  }

  useOnMount(() => {
    const dealIdsToTerminate = dealDetails
      .filter(dealDetail => dealDetail.deal.status !== DealStatus.SERVICING_WITH_INITIATOR)
      .map(dealDetail => dealDetail.deal.dealId)

    if (dealIdsToTerminate.length > 0) {
      onServicingStartTerminateBulk({ dealIds: dealIdsToTerminate })
    }
  })

  const roeWarning = dealDetails.some(dealDetail => dealDetail.deal.rightOfExit === 0)

  return (
    <ModalWindow title={`${title} (${dealDetails.length})`} onEscKey={handleCancel}>
      <Content size="big">
        <FieldsContainer>
          {!isBeforeCutOffTime && (
            <ValidationIssuesContainer>
              {<li>{Locale.translate(Translation.SC_REQUEST_INITIATED_AFTER_CUT_OFF_TIME)}</li>}
            </ValidationIssuesContainer>
          )}
          {roeWarning && (
            <ValidationIssuesContainer>{<li>{Locale.translate(Translation.SC_REQUEST_HAS_NO_ROE)}</li>}</ValidationIssuesContainer>
          )}
          <DialogRow verticalLayout={constants.VerticalLayout.Center}>
            <TableCell>{Locale.translate(Translation.SC_SET_ALL_EFFECTIVE_DATES)}</TableCell>
            <TableCellInput>
              <TradingDatePickerField
                label={''}
                fieldName="effectiveDate"
                currency={firstDealDetail?.deal.currency ?? Currency.EUR}
                minDate={globalMinEffectiveDate ?? undefined}
                maxDate={globalMaxEffectiveDate ?? undefined}
                dateFormat={getDateEditorFormat(userPreference.dateFormat)}
                validationIssues={validationIssues}
                value={effectiveDate}
                onUpdate={(value: string | null) => {
                  setEffectiveDate(value)
                }}
                dataTestId={'ServicingTerminationDialog-EffectiveDateTradingDatePickerField'}
              />
            </TableCellInput>
            <TableCell>
              <TextButton
                type={TextButtonType.Primary}
                disabled={isNil(effectiveDate)}
                onClick={handleSetAllEffectiveDates}
                dataTestId={'ServicingTerminationDialog-ApplyButton'}>
                {Locale.translate(Translation.TC_BUTTON_APPLY)}
              </TextButton>
            </TableCell>
            <TableCell>{Locale.translate(Translation.SC_OR_ENTER_MANUALLY)}</TableCell>
          </DialogRow>
        </FieldsContainer>

        <Table columns={columns} rows={dealDetails} options={options} />
      </Content>
      <ModalFooter>
        <DialogRow>
          <DialogField>
            <TextButton type={TextButtonType.Tertiary} onClick={handleCancel} dataTestId={'ServicingDialog-CancelButton'}>
              {Locale.translate(Translation.TC_BUTTON_CANCEL)}
            </TextButton>
          </DialogField>
          <DialogField flex={0}>
            <TextButton
              type={TextButtonType.Primary}
              disabled={hasValidationErrors || dealDetails.some(d => !canSend(d))}
              onClick={handleSend}
              dataTestId={'ServicingTerminationDialog-SendRequestButton'}>
              {confirmBtnText}
            </TextButton>
          </DialogField>
        </DialogRow>
      </ModalFooter>
    </ModalWindow>
  )
}

const dealIdsSelector = (_: {}, props: ServicingTerminationDialogContainerProps) => props.dealIds

const mapStateToProps = createStructuredSelector({
  dealDetails: createFilteredDealDetailsSelector(dealIdsSelector),
  platformTradeDate: platformTradeDateSelector,
  userPreference: createUserPreferenceSelector()
})

const mapDispatchToProps = {
  onServicingStartTerminateBulk: DealActions.servicingStartTerminateBulk,
  onUpdateField: DealActions.servicingUpdateAttributes,
  onRevert: DealActions.servicingRevertAttributes,
  onCancel: DealActions.servicingCancel,
  onSend: (dealId: string) => DealActions.servicingSend(dealId)
}

export const ServicingTerminationDialog = connect<StateProps, DispatchProps, OwnProps>(
  mapStateToProps,
  mapDispatchToProps
)((props: ServicingTerminationDialogContainerProps) => {
  const { onSend } = props
  return (
    <ServicingTerminationDialogComponent
      {...props}
      title={Locale.translate(Translation.TC_TERMINATION_REQUEST)}
      confirmBtnText={Locale.translate(Translation.TC_BUTTON_SEND_REQUEST)}
      canSend={canServicingSend}
      onSend={async dealIds => {
        dealIds.forEach(id => onSend(id))
        return true
      }}
    />
  )
})

export const ServicingAdminTerminationDialog = connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, {
  ...mapDispatchToProps,
  onServicingStartTerminateBulk: DealActions.servicingStartAdminTerminateBulk,
  onSend: DealActions.servicingAccept
})(
  withModal(({ onSend, showDialog, showModal, ...props }: ServicingTerminationDialogContainerProps & ModalConsumerProps) => {
    const handleOnSend = async (dealIds: string[]) => {
      if (
        (await showDialog({
          buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
          type: NotificationType.Confirm,
          title: Translation.TC_TERMINATE_TRADE,
          messageText: Locale.translate(Translation.SC_TERMINATE_TRADE_CONFIRMATION)
        })) === Translation.TC_BUTTON_YES
      ) {
        dealIds.map(onSend)
        return true
      }
      return false
    }

    return (
      <ServicingTerminationDialogComponent
        {...props}
        title={Locale.translate(Translation.TC_TERMINATE)}
        confirmBtnText={Locale.translate(Translation.TC_ACTION_TERMINATE)}
        onSend={handleOnSend}
        canSend={canServicingAccept}
      />
    )
  })
)
